/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
import { Filter } from '../../../model/Filter';
import { EquipmentTemplateField } from '../../../model/EquipmentTemplateField';
import { EquipmentTemplate } from '../../../model/EquipmentTemplate';
import { BaseController } from '../../BaseController';

class EquipmentTemplateViewController extends BaseController {
    /** @ngInject */
    constructor(configuration, equipmentTemplateService, preventiveSheetTemplateService, alarmCodeService,
        waitSpinnerService, $q, $state, $stateParams, $log, notificationService, domainService, filterService,
        dialogService) {
        super(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log);
        this.equipmentTemplateService = equipmentTemplateService;
        this.preventiveSheetTemplateService = preventiveSheetTemplateService;
        this.alarmCodeService = alarmCodeService;
        this.domainService = domainService;
        this.filterService = filterService;
        this.dialogService = dialogService;
        this.$log.debug('Controller \'EquipmentTemplateView\' loaded');
    }

    $onInit() {
        super.$onInit();

        this.waitSpinnerService.visible = true;
        this.formDisabled = false;
        this.domains = [];
        this.searchLabelMinLength = 3;
        this.translationData = {
            minLength: this.searchLabelMinLength
        };
        this.fieldTypes = ['BOOLEAN', 'LABEL', 'LIST', 'NUMBER', 'URL'].map(dataType => {
            return {
                name: `model.equipmenttemplate.field.type.${dataType}`,
                type: dataType
            };
        });
        this.preventiveSheetTemplates = [];
        this.partName = null;
        this.field = new EquipmentTemplateField();
        this.equipmentTemplate = new EquipmentTemplate();
        // This flag is used to show a warning when saving the template, and fields have been updated.
        this.fieldsUpdated = false;

        // The updateMode flag can be used to know the context in which this controller is running: creation or update.
        let initPromise;
        let domainPromise;
        let preventiveSheetTemplatePromise;
        if (this.$stateParams.hasOwnProperty('id')) {
            this.updateMode = true;
            this.title = 'ui.equipmenttemplate.update';
            initPromise = this.equipmentTemplateService.getEntity(this.$stateParams.id);
            domainPromise = [];
            preventiveSheetTemplatePromise = initPromise.then(
                equipmentTemplate => this.loadPreventiveSheetTemplates(equipmentTemplate.domainId)
            );
        } else {
            this.updateMode = false;
            this.title = 'ui.equipmenttemplate.creation';
            initPromise = this.$q.resolve();
            domainPromise = this.domainService.findEntities({
                params: this.filterService.buildSortingParam({sort: 'name'})
            });
            preventiveSheetTemplatePromise = [];
        }

        this.handleRestServiceCall(this.$q.all({
            entity: initPromise,
            domains: domainPromise,
            preventiveSheetTemplates: preventiveSheetTemplatePromise,
            alarmCodes: this.alarmCodeService.findEntities({
                params: this.filterService.buildSortingParam({sort: 'label'})
            })
        }), data => {
            this.pristineEntity = new EquipmentTemplate(data.entity);
            this.domains = data.domains;
            this.preventiveSheetTemplates = data.preventiveSheetTemplates;
            this.alarmCodes = data.alarmCodes;
            this.reset();
        }).finally(() => {
            this.waitSpinnerService.visible = false;
        });
    }

    /**
     * Loads preventive sheet templates related to a domain.
     *
     * @param {Number} domainId Domain ID.
     */
    loadPreventiveSheetTemplates(domainId) {
        if (domainId) {
            this.preventiveSheetTemplatesDisabled = true;
            this.handleRestServiceCall(
                this.getPreventiveSheetTemplates(domainId),
                preventiveSheetTemplates => {
                    this.preventiveSheetTemplates = preventiveSheetTemplates;
                    // If the selected template is not part of the preventive sheet templates loaded, reset it.
                    if (!this.preventiveSheetTemplates.map(preventiveSheetTemplate => preventiveSheetTemplate.id)
                        .includes(this.equipmentTemplate.preventiveSheetTemplateId)) {
                        this.equipmentTemplate.preventiveSheetTemplateId = undefined;
                    }
                }
            ).catch(() => {
                this.notificationService.pushMessage(
                    'ui.equipmenttemplate.preventivesheettemplateloadingfailed',
                    this.configuration.NOTIFIER_LEVEL_ERROR);
            }).finally(() => {
                this.preventiveSheetTemplatesDisabled = false;
            });
        }
    }

    /**
     * Gets pending alarms for an equipment, and not handled in an action request.
     *
     * @param {Number} equipmentId Equipment ID.
     * @return {Promise} Promise containing the alarms loaded.
     */
    getPreventiveSheetTemplates(domainId) {
        return this.preventiveSheetTemplateService.findEntities({
            params: this.filterService.buildSortingParam({sort: 'name'}),
            data: this.filterService.buildFilters([Filter.equalFilter('domain.id', domainId)])
        });
    }

    addField() {
        if (this.equipmentTemplate.addField(angular.copy(this.field))) {
            this.fieldsUpdated = true;
        } else {
            this.notificationService.pushMessage('ui.equipmenttemplate.addfielderror',
                this.configuration.NOTIFIER_LEVEL_ERROR);
        }
    }

    addPart() {
        if (this.equipmentTemplate.addPart(this.partName)) {
            this.partName = null;
        } else {
            this.notificationService.pushMessage('ui.equipmenttemplate.addparterror',
                this.configuration.NOTIFIER_LEVEL_ERROR);
        }
    }

    removeAlarmCode(alarmCode) {
        this.equipmentTemplate.removeAlarmCode(alarmCode);
    }

    removeField(field) {
        if (this.equipmentTemplate.removeField(field)) {
            this.fieldsUpdated = true;
        }
    }

    removePart(part) {
        this.equipmentTemplate.removePart(part);
    }

    reset() {
        this.equipmentTemplate = angular.copy(this.pristineEntity);
        this.fieldsUpdated = false;
    }

    save() {
        this.formDisabled = true;
        let confirmationPromise;
        if (this.updateMode && this.fieldsUpdated) {
            confirmationPromise = this.showEquipmentUpdateWarningDialog();
        } else {
            confirmationPromise = this.$q.resolve(true);
        }
        confirmationPromise.then(confirmed => {
            if (confirmed) {
                let serviceActionPromise;
                if (this.updateMode) {
                    serviceActionPromise = this.equipmentTemplateService.updateEntity(this.equipmentTemplate);
                } else {
                    serviceActionPromise = this.equipmentTemplateService.createEntity(this.equipmentTemplate);
                }
                this.handleRestServiceCall(serviceActionPromise, () => {
                    const message = 'ui.equipmenttemplate.' + (this.updateMode ? 'updated' : 'created');
                    this.notificationService.pushMessage(message, this.configuration.NOTIFIER_LEVEL_INFO);
                    if (this.updateMode) {
                        this.$state.reload();
                    } else {
                        this.$state.go('gmao.equipment.template.list');
                    }
                });
            }
        }).finally(() => {
            this.formDisabled = false;
        })
    }

    /**
     * Shows a confirmation dialog if fields have been updated.
     *
     * @return {Boolean} If the dialog was confirmed.
     */
    showEquipmentUpdateWarningDialog() {
        return this.dialogService.showConfirmationDialogWithData({
            titleKey: 'ui.equipmenttemplate.update',
            messageKey: 'ui.equipmenttemplate.fieldschanged',
            confirmKey: 'ui.button.yes',
            cancelKey: 'ui.button.no'
        });
    }
}

export const equipmentTemplateViewComponent = {
    template: require('./equipmenttemplate-view.html'),
    controller: EquipmentTemplateViewController
};



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/equipment/component/EquipmentTemplateView.js